/* eslint-disable max-len */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { getFunctions, httpsCallable } from "firebase/functions";

const norm = (v) => String(v ?? "").trim();
const lower = (v) => norm(v).toLowerCase();

const isPaidStatus = (s) => lower(s) === "paid";
const isPendingStatus = (s) => lower(s) === "pending";
const isFailedStatus = (s) => lower(s) === "failed";
const isCancelledStatus = (s) => lower(s) === "cancelled";

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "N/A";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const PAYFAST_MODE = lower(process.env.REACT_APP_PAYFAST_MODE || "sandbox");

const PAYFAST_ENDPOINTS = {
  sandbox: "https://sandbox.payfast.co.za/eng/process",
  live: "https://www.payfast.co.za/eng/process",
};

function postToGateway({ endpoint, fields, listingId, mPaymentId }) {
  try {
    if (listingId) sessionStorage.setItem("pf_last_listingId", listingId);
    if (mPaymentId) sessionStorage.setItem("pf_last_mPaymentId", mPaymentId);
  } catch {}

  const form = document.createElement("form");
  form.method = "POST";
  form.action = endpoint;
  form.style.display = "none";

  Object.entries(fields || {}).forEach(([k, v]) => {
    const val = String(v ?? "").trim();
    if (!val) return;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = k;
    input.value = val;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

function calcSponsoredAmount(promotions = {}) {
  const topAd = Number(promotions?.topAdDays || 0);
  const highlight = !!promotions?.highlight;
  const urgent = !!promotions?.urgent;

  let total = 0;

  if (topAd === 7) total += 30;
  if (topAd === 15) total += 70;
  if (topAd === 31) total += 90;

  if (highlight) total += 70;
  if (urgent) total += 25;

  return total;
}

const SponsoredCheckoutStub = () => {
  const { listingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const [trade, setTrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startingPayfast, setStartingPayfast] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const callFn = useCallback(async (name, payload) => {
    const functions = getFunctions(undefined, "us-central1");
    const fn = httpsCallable(functions, name);
    return await fn(payload);
  }, []);

  const sponsoredPayment = useMemo(
    () => trade?.sponsoredPostPayment || {},
    [trade]
  );

  const paymentStatus = useMemo(
    () => lower(sponsoredPayment?.status || ""),
    [sponsoredPayment]
  );

  const paid = useMemo(() => isPaidStatus(paymentStatus), [paymentStatus]);
  const pending = useMemo(() => isPendingStatus(paymentStatus), [paymentStatus]);
  const failed = useMemo(() => isFailedStatus(paymentStatus), [paymentStatus]);
  const cancelled = useMemo(() => isCancelledStatus(paymentStatus), [paymentStatus]);

  const amountDue = useMemo(() => {
    const fromPayment = Number(sponsoredPayment?.amountZar);
    if (Number.isFinite(fromPayment) && fromPayment > 0) return fromPayment;

    return calcSponsoredAmount(trade?.promotions || {});
  }, [sponsoredPayment, trade]);

  const hasPaidPromo = useMemo(() => {
    const p = trade?.promotions || {};
    return Boolean(
      Number(p?.topAdDays || 0) > 0 ||
      p?.highlight ||
      p?.urgent
    );
  }, [trade]);

  const canStartPayment = useMemo(() => {
    if (!trade) return false;
    if (!hasPaidPromo) return false;
    if (paid) return false;
    if (pending) return false;
    if (!amountDue) return false;
    return true;
  }, [trade, hasPaidPromo, paid, pending, amountDue]);

  const canRetry = useMemo(() => {
    if (!trade) return false;
    if (!hasPaidPromo) return false;
    if (paid) return false;
    if (!amountDue) return false;
    return failed || cancelled;
  }, [trade, hasPaidPromo, paid, failed, cancelled, amountDue]);

  const refreshTrade = useCallback(async ({ silent = false } = {}) => {
    if (!listingId) return;
    if (!silent) setRefreshing(true);

    try {
      const snap = await getDoc(doc(db, "Trades", listingId));
      setTrade(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    } catch {
      if (!silent) toast.error("Failed to refresh sponsored payment status.");
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [listingId]);

  useEffect(() => {
    const load = async () => {
      if (!listingId) {
        toast.error("Invalid sponsored checkout link.");
        navigate("/");
        return;
      }

      if (!currentUser?.uid) {
        navigate("/login", { state: { from: `/checkout/sponsored/${listingId}` } });
        return;
      }

      setLoading(true);

      try {
        const snap = await getDoc(doc(db, "Trades", listingId));

        if (!snap.exists()) {
          toast.error("Listing not found.");
          navigate("/");
          return;
        }

        const t = { id: snap.id, ...snap.data() };

        const ownerUid = norm(t?.ownerUid || t?.ownerId);
        if (!ownerUid || ownerUid !== currentUser.uid) {
          toast.error("You are not authorized to pay for this listing.");
          navigate(`/trade/${listingId}`);
          return;
        }

        setTrade(t);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load sponsored payment details.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [listingId, currentUser?.uid, navigate]);

  useEffect(() => {
    const sp = new URLSearchParams(location.search || "");
    const pf = lower(sp.get("pf") || sp.get("status") || "");
    if (!pf) return;

    if (pf === "return" || pf === "success") {
      toast.info("Payment submitted. Awaiting PayFast confirmation (ITN)...");
      refreshTrade({ silent: true });
    } else if (pf === "cancel" || pf === "cancelled") {
      toast.info("Payment cancelled. You can retry when ready.");
      refreshTrade({ silent: true });
    } else if (pf === "fail" || pf === "failed") {
      toast.info("Payment failed. You can retry when ready.");
      refreshTrade({ silent: true });
    }
  }, [location.search, refreshTrade]);

  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => refreshTrade({ silent: true }), 6000);
    return () => clearInterval(t);
  }, [pending, refreshTrade]);

  const onPayWithPayfast = async () => {
    if (!listingId || !trade) return;

    if (!hasPaidPromo) {
      toast.info("This listing has no paid promotion selected.");
      return;
    }

    if (paid) {
      toast.success("Sponsored payment already confirmed.");
      return;
    }

    if (pending) {
      toast.info("Payment is pending confirmation. Please wait or refresh.");
      return;
    }

    if (!canStartPayment && !canRetry) {
      toast.info("Sponsored payment is not available.");
      return;
    }

    setStartingPayfast(true);
    try {
      const res = await callFn("createSponsoredPostPayment", { tradeId: listingId });
      const data = res?.data || {};
      const pf = data?.payfast || {};
      const fields = pf?.fields;

      const endpoint =
        pf?.endpoint ||
        (PAYFAST_MODE === "live"
          ? PAYFAST_ENDPOINTS.live
          : PAYFAST_ENDPOINTS.sandbox);

      if (PAYFAST_MODE === "live" && endpoint.includes("sandbox.payfast.co.za")) {
        toast.error("PayFast is still configured for sandbox. Check backend PayFast settings.");
        return;
      }

      if (!endpoint || !fields) {
        toast.error("PayFast setup incomplete. Missing redirect details.");
        return;
      }

      const mPaymentId =
        norm(data?.payment?.mPaymentId) || norm(fields?.m_payment_id);

      toast.info("Redirecting to PayFast...");

      postToGateway({
        endpoint,
        fields,
        listingId,
        mPaymentId,
      });
    } catch (e) {
      console.error(e);
      const msg =
        e?.message ||
        e?.details ||
        (typeof e === "string" ? e : "") ||
        "Failed to initiate PayFast payment.";
      toast.error(msg);
    } finally {
      setStartingPayfast(false);
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 840, margin: "40px auto", padding: 20 }}>
        <div style={{ padding: 18, border: "1px solid #e6e6e6", borderRadius: 14 }}>
          <div style={{ fontSize: 18, fontWeight: 900 }}>
            {amountDue ? moneyZAR(amountDue) : "Loading..."}
          </div>
          <div style={{ marginTop: 8, opacity: 0.8 }}>
            Preparing your sponsored listing payment summary.
          </div>
        </div>
      </div>
    );
  }

  if (!trade) return null;

  const statusLabel = paid
    ? "Paid"
    : pending
    ? "Pending confirmation"
    : failed
    ? "Failed"
    : cancelled
    ? "Cancelled"
    : hasPaidPromo
    ? "Ready to pay"
    : "No paid promotion selected";

  const statusHint = paid
    ? "Sponsored listing payment has been confirmed by PayFast ITN."
    : pending
    ? "Payment is awaiting PayFast confirmation (ITN)."
    : failed
    ? "The payment failed. You can retry using PayFast."
    : cancelled
    ? "The payment was cancelled. You can retry using PayFast."
    : hasPaidPromo
    ? "You may proceed with PayFast payment for your selected sponsored options."
    : "This listing does not currently require a sponsored payment.";

  const promotions = trade?.promotions || {};

  return (
    <div style={{ maxWidth: 920, margin: "40px auto", padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.2 }}>
            Sponsored Listing Checkout
            <span style={{ fontSize: 14, fontWeight: 700, marginLeft: 10, opacity: 0.7 }}>(PayFast)</span>
          </div>
          <div style={{ marginTop: 6, opacity: 0.8 }}>
            Review the selected promotion options and complete payment.
          </div>
        </div>

        <div style={{ padding: "8px 12px", borderRadius: 999, border: "1px solid #e6e6e6", fontWeight: 700, fontSize: 13, background: "#fff", whiteSpace: "normal", wordBreak: "break-word", maxWidth: "100%" }}>
          {statusLabel}
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        <div style={{ border: "1px solid #e6e6e6", borderRadius: 16, background: "#fff", padding: 18, flex: "1.2 1 0", minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Order summary</div>

          <div style={{ border: "1px solid #f0f0f0", borderRadius: 14, padding: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 46, height: 46, borderRadius: 12, border: "1px solid #f0f0f0", display: "grid", placeItems: "center", fontSize: 20, background: "#fafafa" }}>
              📢
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: 15, wordBreak: "break-word" }}>{trade.title || "Untitled listing"}</div>
              <div style={{ marginTop: 4, opacity: 0.8, fontSize: 13 }}>
                Listing ID: <span style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{listingId}</span>
              </div>
            </div>

            <div style={{ textAlign: "right", minWidth: 0, wordBreak: "break-word" }}>
              <div style={{ fontSize: 12, opacity: 0.75 }}>Amount due</div>
              <div style={{ fontSize: 18, fontWeight: 900 }}>{moneyZAR(amountDue)}</div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#fafafa" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Selected sponsorship</div>
            <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.55, wordBreak: "break-word" }}>
              <div>Top Ad Days: <strong>{Number(promotions?.topAdDays || 0) || 0}</strong></div>
              <div>Highlight: <strong>{promotions?.highlight ? "Yes" : "No"}</strong></div>
              <div>Urgent: <strong>{promotions?.urgent ? "Yes" : "No"}</strong></div>
            </div>
          </div>

          <div style={{ marginTop: 14, padding: 14, borderRadius: 14, background: "#fafafa" }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Status</div>
            <div style={{ opacity: 0.85, wordBreak: "break-word" }}>{statusHint}</div>
          </div>

          <div style={{ marginTop: 14, border: "1px solid #f0f0f0", borderRadius: 14, padding: 14 }}>
            <div style={{ fontWeight: 900, marginBottom: 6 }}>Pay with PayFast</div>
            <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45, wordBreak: "break-word" }}>
              You will be redirected to PayFast to complete payment securely. Confirmation is completed by server ITN.
            </div>

            <div style={{ marginTop: 12, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                className="tod-btn tod-btn--success nz-touch-min"
                onClick={onPayWithPayfast}
                disabled={startingPayfast || (!canStartPayment && !canRetry)}
                style={{
                  opacity: startingPayfast || (!canStartPayment && !canRetry) ? 0.6 : 1,
                  cursor: startingPayfast || (!canStartPayment && !canRetry) ? "not-allowed" : "pointer",
                }}
              >
                {startingPayfast
                  ? "⏳ Redirecting…"
                  : paid
                  ? "✅ Paid"
                  : pending
                  ? "⏳ Pending confirmation"
                  : failed || cancelled
                  ? "↻ Retry PayFast payment"
                  : "Pay with PayFast"}
              </button>

              <button type="button" className="tod-btn nz-touch-min" onClick={() => navigate(`/trade/${listingId}`)}>
                ⬅ Back to listing
              </button>

              <button type="button" className="tod-btn nz-touch-min" onClick={() => refreshTrade()} disabled={refreshing}>
                {refreshing ? "⏳ Refreshing…" : "🔄 Refresh"}
              </button>
            </div>

            {pending ? (
              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.78 }}>
                Tip: If you just paid, give the ITN a few seconds. This page auto-refreshes while pending.
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ border: "1px solid #e6e6e6", borderRadius: 16, background: "#fff", padding: 18, flex: "0.8 1 0", minWidth: 0, height: "fit-content" }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 10 }}>Secure checkout</div>

          <div style={{ display: "grid", gap: 10 }}>
            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>🔒 Secure payment</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                Sponsored listing payment becomes effective only after PayFast ITN confirmation.
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>📢 Promotion activation</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                After payment, your selected promotion options can be activated automatically.
              </div>
            </div>

            <div style={{ padding: 12, borderRadius: 14, background: "#fafafa" }}>
              <div style={{ fontWeight: 800 }}>⚙️ ITN = source of truth</div>
              <div style={{ marginTop: 4, opacity: 0.85, fontSize: 13, lineHeight: 1.45 }}>
                Return/cancel redirects are for user experience only. The server ITN endpoint should finalize the payment state.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SponsoredCheckoutStub;